import { Grid, useEventCallback } from '@mui/material';
import { Form, Formik } from 'formik';
import CertificateForm from 'front-office/components/certification';
import ModalInfoCertification from 'front-office/components/certification/info-certificate-anpr';
import ModalCertificate from 'front-office/components/certification/request-certificate-modal';
import ModalPrintSuccess from 'front-office/components/shared/request-print';
import { CertificationCrumbs } from 'front-office/constants/certification-anpr-crumbs';
import { transformRequestCertificateData } from 'front-office/utils/transform-request-certificate';
import { CertificateFormVM } from 'front-office/view-models/certificate/certificate-form';
import { Fragment, useCallback, useMemo, useState } from 'react';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { __CERTIFICATION_API_URL__ } from 'shell/constants/environment';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { Certificate } from 'shell/models/certification';
import { HttpResponse } from 'shell/models/http-response';
import { ItemVM } from 'shell/view-models/item';
import * as yup from 'yup';
import { Response6001CertificateVM } from 'front-office/view-models/response-certificate-vm';
import { transform6001Response } from 'front-office/utils/transform-6001-response';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import { DateTime } from 'luxon';
import { validationCertificationSchema } from 'front-office/constants/validation-shema/certification-schema';
// import { openPdf } from 'shell/utils/open-pdf';
import PageButtons from 'front-office/components/shared/buttons-certificate';
import { CommonRequestCertificateInterface } from 'front-office/view-models/common-certificate-form';
import { ModalViewerPDF } from 'shell/components/shared/pdf-modal';
import { AccordionAccountholderFormVM } from 'front-office/view-models/certificate/accountholder-form';
import { AccordionApplicantFormVM } from 'front-office/view-models/certificate/applicant-form';
import { DocumentRadioCertificate } from 'front-office/constants/document-radio-certificate';
import PmiTitle from 'shell/components/shared/title';

const validationSchema = yup
  .object()
  .shape({
    ...validationCertificationSchema,
    accountholder: yup
      .object()
      .shape({})
      .when('certificateInternOnly', {
        is: true,
        then: yup.object().shape({}).required(),
      })
      .test('accountHolder', 'Campo obbligatorio', (values) => {
        const valuesCasted = values as unknown as AccordionAccountholderFormVM;

        // same as applicant
        if (valuesCasted?.sameApplicant) {
          return true;
        }

        // fiscal code required
        if (!valuesCasted?.account?.applicant?.fiscalCode) {
          return new yup.ValidationError(
            'Campo obbligatorio',
            undefined,
            'accountholder.account.applicant.fiscalCode'
          );
        }

        // search required
        if (!valuesCasted?.searchAccountholder) {
          return new yup.ValidationError(
            'Effettuare ricerca',
            undefined,
            'accountholder.searchAccountholder'
          );
        }

        return true;
      }),
  })
  .test('', '', (values) => {
    const valuesCasted = values as unknown as CertificateFormVM;

    const accountHolder = values?.accountholder as AccordionAccountholderFormVM;
    const applicant = values?.applicant as AccordionApplicantFormVM;

    // same as applicant
    if (valuesCasted?.accountholder?.sameApplicant) {
      if (!valuesCasted?.certifiableApplicant) {
        return new yup.ValidationError(
          'Soggetto non certificabile',
          undefined,
          'certifiableApplicant'
        );
      }
    } else {
      if (accountHolder?.account?.applicant?.fiscalCode === applicant?.fiscalCode) {
        return new yup.ValidationError(
          `Richiedente è uguale all'intestatario`,
          undefined,
          'applicant.fiscalCode'
        );
      }
      if (!valuesCasted?.accountholder?.certifiableAccountholder) {
        return new yup.ValidationError(
          'Soggetto non certificabile',
          undefined,
          'certifiableAccountholder'
        );
      }
    }

    return true;
  });

const CertificationPage = (): React.ReactElement => {
  // get the certificate from BE
  const [anprCertificateData, getCertificate] = useHttpFetch<
    HttpResponse<Certificate>,
    Response6001CertificateVM
  >(`${__CERTIFICATION_API_URL__}/richiesta-certificato`, {
    onBootstrap: false,
    options: {
      method: 'POST',
      loading: true,
      errorCatching: true,
    },
    transformer: transform6001Response,
  });

  // ModalCertificate
  const [open, setOpen] = useState<boolean>(false);

  // error visibility
  const [visibility, setVisibility] = useState<{ error?: boolean; warning?: boolean }>({
    error: undefined,
    warning: undefined,
  });

  const castedAnprData = anprCertificateData.data as Response6001CertificateVM;

  // ModalPrintSuccess
  const [modalPrintSuccess, setModalPrintSuccess] = useState<{ open: boolean; total?: number }>({
    open: false,
  });

  // InfoCertification
  const [infoCertification, setInfoCertification] = useState<{ open: boolean; total?: number }>({
    open: false,
  });

  const [pdfModal, setPdfModal] = useState({ data: '', open: false, name: '' });

  // Handle the submit
  const handleSubmit = useCallback(() => {
    setOpen(true);
  }, [setOpen]);

  const { userProfile } = useUserProfile();
  const cashManagement = userProfile?.datiLogin?.profile?.servizio?.gestioneCassa;

  const onRequestCertificate = useEventCallback((values: CertificateFormVM) => {
    // Login data for username of operator
    const data = transformRequestCertificateData(values, userProfile.datiLogin);

    (
      getCertificate({ data }) as ResponsePromise<
        HttpResponse<Certificate>,
        Response6001CertificateVM
      >
    ).then((response) => {
      const castedResponse6001 = response as Response6001CertificateVM;

      if (castedResponse6001.errors?.length > 0) {
        setVisibility({});
        // close ModalCertificate
        setOpen(false);
        return;
      }
      const base64EncodedPDF = castedResponse6001?.certificate;

      const { modality } = values;

      const {
        amountRightsSecretariat = 0.0,
        amountRightsCivilState = 0.0,
        amountFixedRights = 0.0,
        amountExpenses = 0.0,
        amountTaxStamp = 0.0,
      } = modality || {};

      const rights =
        amountExpenses +
        amountFixedRights +
        amountRightsCivilState +
        amountRightsSecretariat +
        amountTaxStamp;

      // close ModalCertificate
      setOpen(false);

      if (base64EncodedPDF) {
        // opens new pdf file on new tab
        // openPdf(base64EncodedPDF);
        setPdfModal({ data: base64EncodedPDF, name: 'Certificazione_ANPR', open: true });
      }

      const parsedTotal = values?.certificateInternOnly
        ? Number.parseFloat('0,00')
        : Number.parseFloat(rights?.toString() || '0,00');

      // const total = (values?.payment || 0) + parsedTotal;

      // setFieldValue('payment', total);

      //open ModalPrintSuccess
      setModalPrintSuccess({ open: false, total: parsedTotal });
    });
  });

  const onYes = useEventCallback(
    (
      values: CommonRequestCertificateInterface,
      setFieldValue: (
        key: keyof CommonRequestCertificateInterface,
        value: CommonRequestCertificateInterface[keyof CommonRequestCertificateInterface]
      ) => void
    ) => {
      const total = (values?.payment || 0) + (modalPrintSuccess?.total || 0);

      setFieldValue('payment', total);

      // close ModalPrintSuccess
      setModalPrintSuccess({ open: false, total: undefined });

      //open InfoCertification
      setInfoCertification({ open: true, total: modalPrintSuccess.total });
    }
  );

  const onVisibility = useCallback(
    (isOpen, type) => {
      const visibilityMessage = type === 'error' && !isOpen ? undefined : isOpen;

      setVisibility((oldState) => ({ ...oldState, [type]: visibilityMessage }));
    },
    [setVisibility]
  );

  const errorMessage = useMemo(() => {
    const errorToVisualize: { visibility?: boolean; title?: string; messages?: Array<ItemVM> } = {
      visibility: false,
      title: '',
    };

    if (castedAnprData?.errors?.length > 0 && visibility.error === undefined) {
      errorToVisualize.title =
        castedAnprData?.errors?.length > 1
          ? `Ci sono ${castedAnprData?.errors?.length} errori`
          : `C'è 1 errore`;
      // errorToVisualize.title = `Ci sono ${(anpr.data as AnprVM<PersonVM>)?.errors?.length} errori`;
      errorToVisualize.messages = castedAnprData?.errors;
      errorToVisualize.visibility = true;
      return errorToVisualize;
    }

    errorToVisualize.visibility = visibility.error;

    return errorToVisualize;
  }, [visibility.error, castedAnprData]);

  return (
    <Fragment>
      <Breadcrumbs items={CertificationCrumbs} base="sportello" />
      <div>
        <PmiTitle title="Certificazione ANPR" />
      </div>
      <SearchMessages
        type={MessageType.Error}
        title={errorMessage.title || ''}
        messages={errorMessage.messages || []}
        visibility={errorMessage.visibility || false}
        onChangeVisibility={onVisibility}
      />
      <SearchMessages
        type={MessageType.Warning}
        title={
          castedAnprData?.warnings?.length > 1
            ? `Ci sono ${castedAnprData?.warnings?.length} warning`
            : `C'è 1 warning`
        }
        messages={castedAnprData?.warnings}
        visibility={
          visibility.warning !== undefined
            ? visibility.warning
            : castedAnprData?.warnings?.length > 0
        }
        onChangeVisibility={onVisibility}
      />
      <div className="pmi-mt-4">
        <div className="pmi-mt-2">
          <Formik
            validationSchema={validationSchema}
            initialValues={
              {
                accountholder: { sameApplicant: true },
                payment: 0,
                dateForm: DateTime.now(),
                certificateInternOnly: cashManagement !== 'S' ? true : false,
                intendedUse: '0',
                generality: {
                  isForeignDocument: DocumentRadioCertificate.IdentityCard,
                },
              } as CertificateFormVM
            }
            onSubmit={(values) => onRequestCertificate(values)}
            validateOnChange
            validateOnBlur
            onReset={() => {
              setVisibility({ error: false, warning: false });
            }}
            // validateOnMount
          >
            {({ values }) => (
              <Form>
                <Grid container spacing={1}>
                  <Grid item xs={12} md={3}>
                    <PageButtons handleRequestCertificate={handleSubmit} hasCleanAccountholder />
                  </Grid>
                  <Grid item xs={12} md={9}>
                    <div className="pmi-px-md-4">
                      <CertificateForm />
                    </div>
                  </Grid>
                </Grid>
                <ModalCertificate open={open} onClose={setOpen} />
                <ModalPrintSuccess
                  open={modalPrintSuccess.open}
                  onClose={() => setModalPrintSuccess({ open: false })}
                  onYes={onYes}
                />
                <ModalInfoCertification
                  open={infoCertification.open}
                  onClose={() => setInfoCertification({ open: false })}
                  amountCertifate={infoCertification.total?.toFixed(2)}
                  amountTotal={values.payment?.toFixed(2)}
                />
                <ModalViewerPDF
                  open={pdfModal.open}
                  data={pdfModal.data}
                  name={pdfModal.name}
                  title="Certificazione"
                  onClose={() => {
                    setPdfModal({ data: '', open: false, name: '' });
                    setModalPrintSuccess((oldState) => ({ ...oldState, open: true }));
                  }}
                />
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </Fragment>
  );
};

CertificationPage.displayName = 'Certification page';

export default CertificationPage;
